import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../services/api";

const CITE_TOKEN_RE = /(DOC|E|G|D|F)\s*(\d+)/gi;
const CITE_MARK_RE =
  /(?:【\s*([^】]+?)\s*】|\[\s*((?:DOC|E|G|D|F)\s*\d+(?:\s*,\s*(?:DOC|E|G|D|F)\s*\d+)*)\s*\])/gi;

export function canonicalCitationKey(raw) {
  const text = String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  if (text.startsWith("DOC") && /^\d+$/.test(text.slice(3))) {
    return `D${text.slice(3)}`;
  }
  return text;
}

export function parseCitationTokens(inner) {
  const keys = [];
  const seen = new Set();
  const body = String(inner || "");
  CITE_TOKEN_RE.lastIndex = 0;
  let match;
  while ((match = CITE_TOKEN_RE.exec(body))) {
    const key = canonicalCitationKey(`${match[1]}${match[2]}`);
    if (key && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  return keys;
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function citationChipLabel(cite, fallbackKey) {
  if (!cite) return fallbackKey || "";
  const explicit = String(cite.chip_label || "").trim();
  if (explicit) return explicit;
  const title = String(cite.title || cite.source_title || cite.label || "").trim();
  if (cite.kind === "catalog") {
    return (title.split(" (")[0] || title || fallbackKey || "Catalog").trim();
  }
  return title || fallbackKey || "Source";
}

/**
 * Replace 【E1】 / 【DOC1】 / 【G2, G3】 markers with clickable chips.
 */
export function injectCitationChipsHtml(html, citations = []) {
  const byKey = {};
  (citations || []).forEach((c) => {
    const k = canonicalCitationKey(c?.key);
    if (!k) return;
    byKey[k] = c;
    if (k.startsWith("D") && /^\d+$/.test(k.slice(1))) {
      byKey[`DOC${k.slice(1)}`] = c;
    }
  });
  if (!Object.keys(byKey).length) {
    return html;
  }
  CITE_MARK_RE.lastIndex = 0;
  return String(html || "").replace(CITE_MARK_RE, (_m, a, b) => {
    const keys = parseCitationTokens(a || b || "");
    if (!keys.length) return "";
    return keys
      .map((key) => {
        const cite = byKey[key] || byKey[canonicalCitationKey(key)];
        if (!cite) return "";
        const label = citationChipLabel(cite, key);
        const title = String(cite.label || cite.title || label).replace(
          /"/g,
          "&quot;"
        );
        return `<button type="button" class="agent-cite-chip" data-cite-key="${escapeHtml(
          String(cite.key || key)
        )}" title="${title}">${escapeHtml(label)}</button>`;
      })
      .join("");
  });
}

function filenameFromDisposition(header, fallback) {
  const value = String(header || "");
  const star = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* keep fallback */
    }
  }
  const quoted = value.match(/filename="([^"]+)"/i);
  if (quoted) return quoted[1];
  const plain = value.match(/filename=([^;]+)/i);
  if (plain) return plain[1].replace(/['"]/g, "").trim();
  return fallback;
}

export async function openCitedSourceFile(cite) {
  const sourceVersionId = String(cite?.source_version_id || "").trim();
  if (!sourceVersionId) {
    throw new Error("This citation has no ingested file.");
  }
  const response = await apiFetch(
    `/api/knowledge/sources/${encodeURIComponent(sourceVersionId)}/file`
  );
  if (!response.ok) {
    let detail = "Could not open this file.";
    try {
      const err = await response.json();
      detail = err.detail || err.message || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const filename = filenameFromDisposition(
    response.headers.get("Content-Disposition"),
    response.headers.get("X-Source-File-Name") || "source"
  );
  const origin = String(
    response.headers.get("X-Source-File-Origin") || ""
  ).toLowerCase();
  const mime = String(response.headers.get("Content-Type") || blob.type || "");
  const objectUrl = window.URL.createObjectURL(blob);
  const isPdf = mime.includes("pdf") || /\.pdf$/i.test(filename);
  if (isPdf) {
    window.open(objectUrl, "_blank", "noopener,noreferrer");
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
  } else {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 2_000);
  }
  return { filename, origin, mime };
}

export function CitationInspector({
  cite,
  onClose,
  onNavigateEntity,
  onOpenSourceFile,
}) {
  const [fileBusy, setFileBusy] = useState(false);
  const [fileError, setFileError] = useState("");
  const [fileNote, setFileNote] = useState("");
  useEffect(() => {
    setFileBusy(false);
    setFileError("");
    setFileNote("");
  }, [cite?.key, cite?.source_version_id]);
  if (!cite) return null;
  const entityId =
    cite.entity_id ||
    (Array.isArray(cite.entity_ids) && cite.entity_ids[0]) ||
    null;
  const sourceVersionId = cite.source_version_id || null;
  const openFile =
    typeof onOpenSourceFile === "function"
      ? onOpenSourceFile
      : openCitedSourceFile;
  const handleOpenFile = async () => {
    setFileBusy(true);
    setFileError("");
    setFileNote("");
    try {
      const result = await openFile(cite);
      if (result?.origin === "extracted") {
        setFileNote(
          "The original upload was not kept, so this opens an extracted copy in Word, Adobe, or your default app."
        );
      }
    } catch (err) {
      setFileError(err?.message || "Could not open this file.");
    } finally {
      setFileBusy(false);
    }
  };
  return (
    <div className="agent-cite-detail">
      <div className="agent-cite-detail-header">
        <strong>{citationChipLabel(cite, cite.key)}</strong>
        <span className="agent-cite-kind">{cite.kind || "source"}</span>
        {typeof onClose === "function" ? (
          <button type="button" className="agent-cite-close" onClick={onClose}>
            ×
          </button>
        ) : null}
      </div>
      <div className="agent-cite-label">{cite.label || cite.title}</div>
      {cite.excerpt ? (
        <blockquote className="agent-cite-excerpt">{cite.excerpt}</blockquote>
      ) : null}
      <div className="agent-cite-meta">
        {cite.at ? <div>When: {String(cite.at).slice(0, 19)}</div> : null}
        {cite.actor_name || cite.actor_id ? (
          <div>By: {cite.actor_name || "a user"}</div>
        ) : null}
        {cite.source_kind ? <div>Kind: {cite.source_kind}</div> : null}
        {cite.source_title || cite.title ? (
          <div>Document: {cite.source_title || cite.title}</div>
        ) : null}
      </div>
      <div className="agent-cite-actions">
        {sourceVersionId ? (
          <button
            type="button"
            className="agent-cite-open-source"
            disabled={fileBusy}
            onClick={handleOpenFile}
          >
            {fileBusy ? "Opening…" : "Open file"}
          </button>
        ) : null}
        {entityId && typeof onNavigateEntity === "function" ? (
          <button
            type="button"
            className="agent-cite-open-entity"
            onClick={() => onNavigateEntity(entityId, cite)}
          >
            Open in catalog
          </button>
        ) : null}
      </div>
      {fileNote ? <div className="agent-cite-file-note">{fileNote}</div> : null}
      {fileError ? <div className="agent-cite-file-error">{fileError}</div> : null}
    </div>
  );
}

/**
 * Sources list + optional selected citation detail under an assistant message.
 */
export default function AgentCitationsPanel({
  citations = [],
  conflicts = [],
  selectedKey = null,
  onSelectKey,
  onNavigateEntity,
  hideInlineDetail = false,
  onOpenSourceFile,
}) {
  const list = useMemo(
    () => (Array.isArray(citations) ? citations.filter((c) => c && c.key) : []),
    [citations]
  );
  const [internalKey, setInternalKey] = useState(null);
  const activeKey = selectedKey != null ? selectedKey : internalKey;
  const setActiveKey = (key) => {
    if (typeof onSelectKey === "function") onSelectKey(key);
    else setInternalKey(key);
  };
  const selected = list.find(
    (c) =>
      canonicalCitationKey(c.key) === canonicalCitationKey(activeKey || "")
  );

  if (!list.length) return null;

  return (
    <div className="agent-citations-panel">
      <div className="agent-citations-title">Sources</div>
      {Array.isArray(conflicts) && conflicts.length > 0 ? (
        <div className="agent-citations-conflicts">
          Newer evidence is preferred; older contradictory sources are listed and cited.
        </div>
      ) : null}
      <ul className="agent-citations-list">
        {list.map((c) => (
          <li key={c.key}>
            <button
              type="button"
              className={`agent-cite-row ${c.is_primary ? "primary" : ""} ${
                canonicalCitationKey(activeKey || "") ===
                canonicalCitationKey(c.key)
                  ? "selected"
                  : ""
              }`}
              onClick={() =>
                setActiveKey(
                  canonicalCitationKey(activeKey || "") ===
                    canonicalCitationKey(c.key)
                    ? null
                    : c.key
                )
              }
            >
              <span className="agent-cite-key">{c.key}</span>
              <span className="agent-cite-row-label">
                {c.label || c.title || citationChipLabel(c, c.key)}
                {c.is_primary ? " · primary" : ""}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {selected && !hideInlineDetail ? (
        <CitationInspector
          cite={selected}
          onClose={() => setActiveKey(null)}
          onNavigateEntity={onNavigateEntity}
          onOpenSourceFile={onOpenSourceFile}
        />
      ) : null}
    </div>
  );
}

export function citationKeyFromClickTarget(target) {
  const el =
    target && typeof target.closest === "function"
      ? target.closest("[data-cite-key]")
      : null;
  return el
    ? canonicalCitationKey(el.getAttribute("data-cite-key") || "")
    : "";
}
